"use client";
import useTranslate from "@app/_hooks/useTranslate";

import { Paragraph } from "@app/_components/ui";
import * as Accordion from "@radix-ui/react-accordion";
import { ChevronDown } from "lucide-react";
import { useState } from "react";

import { cn } from "@/lib/utils";

import type { DELIVERY_ITEM } from "../Stock.types";
import { type StockStatusProps } from "../Stock.types";
import StockDialog from "../StockDialog";
import StockIndicator from "./StockIndicator";

export default function DeliveryItem({
  status,
  config,
}: {
  status: DELIVERY_ITEM;
  config: StockStatusProps;
}) {
  const t = useTranslate();
  const [popup, setPopup] = useState<boolean>(false);
  const { type, item, className, stockInfo, madeToOrderShipDate, ...props } =
    config;
  if (type === "pdp") {
    return (
      <>
        <Accordion.Root type="multiple" className={cn("text-base", className)}>
          <Accordion.Item value={item?.id ?? "1"} {...props}>
            <Accordion.Header asChild>
              <div className="inline-flex flex-wrap items-start gap-2">
                <StockIndicator status={status} />
                <span className="inline-block">
                  <strong className="font-normal">
                    {t("availableForOrder")}
                  </strong>
                  {", "}
                  {status.date &&
                    t("shipsAtTheEarliest", { X: status.date })}
                </span>
                <Accordion.Trigger className="group inline-flex items-center gap-1 whitespace-nowrap underline">
                  {t("moreInfo")}
                  <ChevronDown className="h-3 w-3 transition-all group-data-[state=closed]:rotate-0 group-data-[state=open]:rotate-180" />
                </Accordion.Trigger>
              </div>
            </Accordion.Header>
            <Accordion.Content className="mt-2 overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
              <Paragraph className="text-gray-500">
                {t("deliveryItemExplanation")}{" "}
                {stockInfo && (
                  <button
                    className="text-black underline"
                    onClick={() => setPopup(true)}
                  >
                    {t("readMore")}
                  </button>
                )}
              </Paragraph>
            </Accordion.Content>
          </Accordion.Item>
        </Accordion.Root>
        {stockInfo && (
          <StockDialog
            open={popup}
            onClose={() => setPopup(false)}
            block={stockInfo}
          />
        )}
      </>
    );
  }
  if (type === "plp") {
    return null;
  }
  // Made-to-order line ("1B" Piece A): a discreet muted-grey note, no dot — it
  // reads as information, not a warning, and deliberately shares Piece B's grey.
  // When the line has an estimated ship date, append "…, skickas tidigast <date>"
  // (date kept on one line). The dot is intentionally dropped (it was hidden for
  // DeliveryItem anyway).
  return (
    <div
      className={cn(
        "mt-2 text-[13px] leading-[1.45] text-[#9a9893]",
        className,
      )}
      {...props}
    >
      {t("madeToOrder")}
      {madeToOrderShipDate && (
        <>
          {", "}
          {t("shipsAtEarliest")}{" "}
          <span className="whitespace-nowrap">{madeToOrderShipDate}</span>
        </>
      )}
    </div>
  );
}
